import React, { useEffect, useRef, useState } from 'react';
import { ADSENSE_CLIENT, isProductionAdHost } from '../config/ads';

interface AdUnitProps {
  slotId: string;
  className?: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal' | 'vertical';
  client?: string;
  label?: string;
  theme?: 'light' | 'dark';
}

const AdUnit: React.FC<AdUnitProps> = ({ 
  slotId, 
  className = '', 
  format = 'auto',
  client = ADSENSE_CLIENT,
  label = 'Advertisement',
  theme = 'dark'
}) => {
  const adRef = useRef<HTMLModElement>(null);
  const [adStatus, setAdStatus] = useState<'loading' | 'filled' | 'unfilled'>('loading');
  const initialized = useRef(false);
  const isDark = theme === 'dark';

  useEffect(() => {
    initialized.current = false;
    setAdStatus('loading');
  }, [slotId]);

  useEffect(() => {
    if (!adRef.current) return;
    if (initialized.current) return;

    const element = adRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const pushAd = () => {
      if (initialized.current) return;
      if (element && element.offsetParent !== null && element.offsetWidth > 0 && element.isConnected) {
        try {
          // Assign the queue to window: adsbygoogle.js loads async and drains this
          // array when it arrives. A throwaway [] would silently drop the request.
          const w = window as unknown as { adsbygoogle?: object[] };
          w.adsbygoogle = w.adsbygoogle || [];
          w.adsbygoogle.push({});
          initialized.current = true;
          if (resizeObserver) resizeObserver.disconnect();
        } catch (e) {
          console.error('AdSense push error:', e);
        }
      }
    };

    try {
      resizeObserver = new ResizeObserver((entries) => {
        window.requestAnimationFrame(() => {
          if (!initialized.current) {
            for (const entry of entries) {
              if (entry.contentRect.width > 0 && element.offsetParent !== null) {
                pushAd();
              }
            }
          }
        });
      });
      resizeObserver.observe(element);
    } catch (e) {
      if (element.offsetWidth > 0) pushAd();
    }

    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ad-status') {
          const status = element.getAttribute('data-ad-status');
          if (status === 'unfilled') {
            setAdStatus('unfilled');
            if (resizeObserver) resizeObserver.disconnect();
          } else if (status === 'filled') {
            setAdStatus('filled');
          }
        }
      }
    });
    mutationObserver.observe(element, { attributes: true });

    const timer = setTimeout(() => pushAd(), 100);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
      clearTimeout(timer);
    };
  }, [slotId]);

  // The <ins> must always be in the DOM: AdSense can only fill a slot it can see.
  // Space is reserved up front (no CLS when the ad arrives) and released only if
  // AdSense reports the slot unfilled.
  if (adStatus === 'unfilled') return null;

  const adTest = typeof window !== 'undefined' && !isProductionAdHost(window.location.hostname) ? 'on' : undefined;

  return (
    <div
      className={`ad-container w-full flex flex-col items-center justify-center my-4 ${className}`}
      data-print="hide"
      aria-label={label}
      role="complementary"
    >
      <span className={`text-[9px] font-semibold text-slate-500 uppercase tracking-widest mb-2 select-none ${adStatus === 'filled' ? 'opacity-50' : 'opacity-0'}`}>
        {label}
      </span>
      <div className={`w-full flex justify-center items-center rounded-lg overflow-hidden ${isDark ? 'bg-slate-800/50' : 'bg-slate-50'}`} style={{ minHeight: 90 }}>
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', width: '100%', minHeight: 90, textAlign: 'center' }}
          data-ad-client={client}
          data-ad-slot={slotId}
          data-ad-format={format}
          data-full-width-responsive="true"
          data-adtest={adTest}
        />
      </div>
    </div>
  );
};

export default AdUnit;
